import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SellerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [chart, setChart] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const [s, i, c] = await Promise.all([
      api.get("/seller/stats"),
      api.get("/seller/itineraries"),
      api.get("/seller/sales-chart"),
    ]);
    setStats(s.data);
    setItems(i.data);
    setChart(c.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const handleUnpublish = async (id) => {
    try {
      await api.post(`/seller/itineraries/${id}/unpublish`);
      toast.success("Unpublished.");
      await load();
    } catch { toast.error("Could not unpublish"); }
  };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/seller/itineraries/${id}`);
      toast.success("Deleted.");
      setConfirmDelete(null);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="seller-dashboard">
      <Navbar />
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pt-10 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="eyebrow">◆ Verified creator · {user?.name}</span>
            <h1 className="h-display text-[40px] sm:text-[52px] mt-3">Good morning.</h1>
            <p className="text-[14px] text-[var(--ink2)] mt-1">
              {stats ? `${stats.total_sales} total sales · ₹${stats.total_earnings.toLocaleString()} earned` : "Loading…"}
            </p>
          </div>
          <button
            data-testid="new-itinerary-cta"
            onClick={() => navigate("/seller/new")}
            className="btn btn-solid"
          >
            ＋ New itinerary
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-row">
          <Stat label="All-time earnings" value={stats ? `₹${stats.total_earnings.toLocaleString()}` : "—"} />
          <Stat label="This month" value={stats ? `₹${stats.month_earnings.toLocaleString()}` : "—"} />
          <Stat label="Total sales" value={stats ? stats.total_sales.toString() : "—"} />
          <Stat label="Live routes" value={stats ? stats.live_itineraries.toString() : "—"} />
        </div>

        {/* Chart */}
        <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-6 mb-8" data-testid="sales-chart">
          <span className="eyebrow">◆ Sales · last 30 days</span>
          <div className="text-[22px] font-extrabold tracking-[-0.02em] mt-1 mb-4">
            {stats ? `${chart.reduce((a, b) => a + b.sales, 0)} sales · ₹${chart.reduce((a, b) => a + b.revenue, 0).toLocaleString()}` : "—"}
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "var(--ink3)", fontFamily: "var(--mono)" }}
                  axisLine={{ stroke: "var(--line)" }}
                  tickLine={false}
                  tickFormatter={(d) => d.slice(5)}
                  minTickGap={30}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "var(--ink3)", fontFamily: "var(--mono)" }}
                  axisLine={false}
                  tickLine={false}
                  width={32}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--card)",
                    border: "1.5px solid var(--line)",
                    borderRadius: 12,
                    fontFamily: "var(--mono)",
                    fontSize: 11,
                  }}
                />
                <Line type="monotone" dataKey="sales" stroke="var(--red)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Itineraries table */}
        <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] overflow-hidden" data-testid="itineraries-table">
          <div className="px-6 py-4 border-b border-[var(--line)] flex items-center justify-between">
            <div>
              <span className="eyebrow">◆ Your itineraries</span>
              <div className="text-[16px] font-extrabold mt-1">{items.length} {items.length === 1 ? "route" : "routes"}</div>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-[14px] text-[var(--ink3)]">
              No itineraries yet. The builder ships in the next session.
            </div>
          ) : (
            <div className="divide-y divide-[var(--line)]">
              <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-3 mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)]">
                <span>Title</span><span>Region</span><span>Sales</span><span>Earnings</span><span>Status</span><span>Actions</span>
              </div>
              {items.map((it) => (
                <div
                  key={it.itinerary_id}
                  data-testid={`row-${it.slug}`}
                  className="grid sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-4 items-center"
                >
                  <div>
                    <div className="text-[14px] font-extrabold leading-tight">{it.title || "(Untitled draft)"}</div>
                    {it.status === "live" && (
                      <Link to={`/itinerary/${it.slug}`} className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)] hover:text-[var(--red)] transition-colors">
                        ◆ View public →
                      </Link>
                    )}
                  </div>
                  <div className="mono text-[12px] text-[var(--ink2)]">{it.destination_country || "—"}</div>
                  <div className="text-[14px] font-bold">{it.total_sales}</div>
                  <div className="text-[14px] font-bold text-[var(--red)]">₹{Number(it.earnings).toLocaleString()}</div>
                  <div>
                    <span className={`mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full font-bold ${it.status === "live" ? "bg-[var(--red)] text-white" : "bg-[var(--line)] text-[var(--ink2)]"}`}>
                      {it.status}
                    </span>
                  </div>
                  <div className="flex gap-2 justify-end flex-wrap">
                    <Link to={`/seller/edit/${it.itinerary_id}`} data-testid={`edit-${it.slug}`} className="btn btn-ghost btn-sm">Edit</Link>
                    {it.status === "live" ? (
                      <button onClick={() => handleUnpublish(it.itinerary_id)} data-testid={`unpublish-${it.slug}`} className="btn btn-paper btn-sm">Unpublish</button>
                    ) : null}
                    <button onClick={() => setConfirmDelete(it)} data-testid={`delete-${it.slug}`} className="btn btn-ghost btn-sm text-[var(--red)] hover:bg-[var(--red)] hover:text-white">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="mt-8 text-[12px] text-[var(--ink3)] mono tracking-[0.1em]">
          Stripe Connect payouts ship in Session 4.
        </p>
      </section>

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-modal">
          <div className="bg-[var(--paper)] border-[1.5px] border-[var(--line)] rounded-[18px] max-w-[420px] w-full p-7" onClick={(e) => e.stopPropagation()}>
            <span className="eyebrow">◆ Confirm delete</span>
            <h3 className="h-display text-[26px] mt-2 leading-tight">Delete "{confirmDelete.title || "(Untitled draft)"}"?</h3>
            <p className="text-[13px] text-[var(--ink2)] mt-3 leading-[1.6]">This cannot be undone. Buyers who already purchased will lose access.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmDelete(null)} className="btn btn-ghost flex-1">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.itinerary_id)} data-testid="confirm-delete-yes" className="btn btn-solid flex-1">Yes, delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-5">
    <span className="eyebrow">{label}</span>
    <div className="serif text-[34px] mt-1 leading-none">{value}</div>
  </div>
);
